import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaCog, FaSave, FaExpand, FaCheck, FaExclamationTriangle, FaAlignLeft, FaStream, FaCode } from "react-icons/fa";
import { Drawer, Modal, Button, ToggleSwitch, Alert, Accordion } from 'components/common';
import JsonTree from "components/common/JsonTree";
import ModelBadge from "components/common/ModelBadge";
import { getExtractionPrompts, updateExtractionPrompt } from "redux/document/extractionAdminSlice";

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const labelClass = "tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500";
const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");
const PROVIDERS = [
  { value: "", label: "(inherit default AI_PROVIDER)" },
  { value: "claude", label: "Claude (Anthropic)" },
  { value: "openai", label: "OpenAI" },
  { value: "ollama", label: "Ollama" },
];

// Shared monospace metrics so the gutter, highlight layer, and textarea line up.
const CODE_STYLE = { fontSize: 12.5, lineHeight: "20px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", whiteSpace: "pre", tabSize: 2 };
const CODE_PAD = { padding: "8px 12px" };

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Dark-theme JSON highlighter (matches the raw_text/llm_result viewer). Escapes
// first, then wraps tokens — safe from injection since <>& are neutralized.
const highlightJson = (json) =>
  escapeHtml(json).replace(
    /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?)|(\btrue\b|\bfalse\b)|(\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (m, str, bool, nul, num) => {
      if (str) return `<span class="${/:\s*$/.test(str) ? "tw-text-sky-300" : "tw-text-emerald-300"}">${str}</span>`;
      if (bool) return `<span class="tw-text-fuchsia-300">${bool}</span>`;
      if (nul) return `<span class="tw-text-slate-500">${nul}</span>`;
      return `<span class="tw-text-amber-300">${num}</span>`;
    }
  );

// Editable code editor with a synced line-number gutter and syntax highlighting:
// a transparent-text <textarea> (caret visible) overlays a colored <pre>. wrap="off"
// keeps logical lines on one row so the numbers stay aligned.
const HighlightedCodeEditor = ({ value, onChange, height, placeholder, invalid, json }) => {
  const taRef = useRef(null);
  const preRef = useRef(null);
  const gutterRef = useRef(null);
  const text = value || "";
  const lineCount = useMemo(() => Math.max(text.split("\n").length, 1), [text]);
  const numbers = useMemo(() => Array.from({ length: lineCount }, (_, i) => i + 1).join("\n"), [lineCount]);
  // Trailing newline so the last line renders in the highlight layer.
  const html = useMemo(() => (json ? highlightJson(text) : escapeHtml(text)) + "\n", [text, json]);

  const syncScroll = () => {
    const ta = taRef.current;
    if (!ta) return;
    if (preRef.current) { preRef.current.scrollTop = ta.scrollTop; preRef.current.scrollLeft = ta.scrollLeft; }
    if (gutterRef.current) gutterRef.current.scrollTop = ta.scrollTop;
  };

  return (
    <div
      className={`tw-flex tw-rounded-md tw-border tw-border-solid tw-overflow-hidden tw-bg-slate-900 focus-within:tw-ring-2 focus-within:tw-ring-blue-500 ${invalid ? "tw-border-red-400" : "tw-border-slate-700"}`}
      style={{ height }}
    >
      <pre
        ref={gutterRef}
        aria-hidden="true"
        className="tw-m-0 tw-py-2 tw-px-2 tw-text-right tw-text-slate-500 tw-bg-slate-800 tw-select-none tw-overflow-hidden tw-border-0"
        style={{ ...CODE_STYLE, minWidth: 40, borderRight: "1px solid #334155" }}
      >
        {numbers}
      </pre>
      <div className="tw-relative tw-flex-1 tw-overflow-hidden">
        <pre
          ref={preRef}
          aria-hidden="true"
          className="tw-absolute tw-inset-0 tw-m-0 tw-overflow-hidden tw-pointer-events-none tw-text-slate-100"
          style={{ ...CODE_STYLE, ...CODE_PAD }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          onScroll={syncScroll}
          placeholder={placeholder}
          spellCheck={false}
          wrap="off"
          className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-bg-transparent tw-border-0 tw-resize-none tw-overflow-auto tw-placeholder-slate-500 focus:tw-outline-none"
          style={{ ...CODE_STYLE, ...CODE_PAD, color: "transparent", caretColor: "#e2e8f0" }}
        />
      </div>
    </div>
  );
};
HighlightedCodeEditor.propTypes = { value: PropTypes.string, onChange: PropTypes.func, height: PropTypes.string, placeholder: PropTypes.string, invalid: PropTypes.bool, json: PropTypes.bool };

// Line-numbered editor + Expand (large modal) + (for JSON) live validity + Format.
const CodeField = ({ label, value, onChange, json = false, height = "200px", placeholder }) => {
  const [expanded, setExpanded] = useState(false);
  const [treeView, setTreeView] = useState(false);

  const jsonError = useMemo(() => {
    if (!json || !value || !value.trim()) return null;
    try { JSON.parse(value); return null; } catch (e) { return e.message; }
  }, [json, value]);

  // Parsed schema for the collapsible tree view (undefined when not valid JSON).
  const parsed = useMemo(() => {
    if (!json || !value || !value.trim()) return undefined;
    try { return JSON.parse(value); } catch { return undefined; }
  }, [json, value]);

  const canTree = json && parsed !== undefined;
  const showTree = canTree && treeView;

  const format = () => {
    try { onChange(JSON.stringify(JSON.parse(value), null, 2)); }
    catch { toast.error("Invalid JSON — cannot format."); }
  };

  const Toolbar = (
    <div className="tw-flex tw-items-center tw-gap-3">
      {json && value && value.trim() && (
        jsonError
          ? <span className="tw-text-[11px] tw-text-red-600 tw-inline-flex tw-items-center tw-gap-1"><FaExclamationTriangle size={10} /> Invalid JSON</span>
          : <span className="tw-text-[11px] tw-text-emerald-600 tw-inline-flex tw-items-center tw-gap-1"><FaCheck size={10} /> Valid</span>
      )}
      {canTree && (
        <button type="button" onClick={() => setTreeView((t) => !t)} className="tw-text-[11px] tw-text-blue-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-cursor-pointer">
          {showTree ? <><FaCode size={10} /> Raw</> : <><FaStream size={10} /> Tree</>}
        </button>
      )}
      {json && !showTree && (
        <button type="button" onClick={format} className="tw-text-[11px] tw-text-blue-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-cursor-pointer">
          <FaAlignLeft size={10} /> Format
        </button>
      )}
    </div>
  );

  return (
    <div className="tw-mb-3">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
        <label className={labelClass}>{label}</label>
        <div className="tw-flex tw-items-center tw-gap-3">
          {Toolbar}
          <button type="button" onClick={() => setExpanded(true)} className="tw-text-[11px] tw-text-blue-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-cursor-pointer">
            <FaExpand size={10} /> Expand
          </button>
        </div>
      </div>

      {showTree
        ? <JsonTree data={parsed} height={height} />
        : <HighlightedCodeEditor value={value} onChange={onChange} height={height} placeholder={placeholder} invalid={!!jsonError} json={json} />}
      {json && jsonError && <p className="tw-text-[11px] tw-text-red-600 tw-mt-1 tw-mb-0">{jsonError}</p>}

      <Modal
        show={expanded}
        onHide={() => setExpanded(false)}
        title={label}
        fullscreen
        footer={<Button size="sm" variant="primary" onClick={() => setExpanded(false)}>Done</Button>}
      >
        <div className="tw-flex tw-items-center tw-justify-end tw-mb-2 tw-min-h-[18px]">{Toolbar}</div>
        {showTree
          ? <JsonTree data={parsed} height="max(320px, calc(100dvh - 190px))" />
          : <HighlightedCodeEditor value={value} onChange={onChange} height="max(320px, calc(100dvh - 190px))" placeholder={placeholder} invalid={!!jsonError} json={json} />}
      </Modal>
    </div>
  );
};
CodeField.propTypes = { label: PropTypes.string, value: PropTypes.string, onChange: PropTypes.func, json: PropTypes.bool, height: PropTypes.string, placeholder: PropTypes.string };

// Collapsible header: document type + provider/model summary + active state.
const PromptHeader = ({ prompt }) => (
  <span className="tw-flex tw-flex-1 tw-items-center tw-justify-between tw-gap-3 tw-pr-2">
    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-font-semibold tw-text-slate-800">
      <FaCog className="tw-text-slate-400" size={13} /> {humanize(prompt.document_type)}
    </span>
    <span className="tw-inline-flex tw-items-center tw-gap-2">
      <ModelBadge model={prompt.llm_model} size={12} className="tw-text-[11px] tw-text-slate-500" />
      <span className={`tw-text-[11px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl ${prompt.active ? "tw-bg-emerald-100 tw-text-emerald-700" : "tw-bg-slate-100 tw-text-slate-500"}`}>
        {prompt.active ? "Active" : "Inactive"}
      </span>
    </span>
  </span>
);
PromptHeader.propTypes = { prompt: PropTypes.object.isRequired };

// Editable body for one document_extraction_prompts row.
const PromptEditBody = ({ prompt, onSaved }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({});
  const [schemaText, setSchemaText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      name:       prompt.name || "",
      provider:   prompt.provider || "",
      llm_model:  prompt.llm_model || "",
      use_vision: !!prompt.use_vision,
      active:     !!prompt.active,
      prompt:     prompt.prompt || "",
    });
    setSchemaText(prompt.json_schema ? JSON.stringify(prompt.json_schema, null, 2) : "");
  }, [prompt]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    let json_schema = null;
    if (schemaText.trim()) {
      try { json_schema = JSON.parse(schemaText); }
      catch { toast.error("JSON schema is not valid JSON."); return; }
    }
    setSaving(true);
    try {
      await dispatch(updateExtractionPrompt({ id: prompt.id, data: { ...form, json_schema } })).unwrap();
      onSaved?.();
    } catch (e) { /* toast handled in slice */ }
    finally { setSaving(false); }
  };

  return (
    <div className="tw-p-4">
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-mb-3">
        <div>
          <label className={`${labelClass} tw-mb-1`}>Name</label>
          <input className={inputClass} value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className={`${labelClass} tw-mb-1`}>Provider</label>
          <select className={inputClass} value={form.provider || ""} onChange={(e) => set("provider", e.target.value)}>
            {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className={`${labelClass} tw-mb-1`}>Model</label>
          <input className={inputClass} value={form.llm_model || ""} onChange={(e) => set("llm_model", e.target.value)} placeholder="gpt-4o-mini / claude-sonnet-4-6" />
        </div>
        <div className="tw-flex tw-items-center tw-gap-6 tw-pt-5">
          <ToggleSwitch value={!!form.use_vision} label="Use vision" onChange={(v) => set("use_vision", v)} />
          <ToggleSwitch value={!!form.active} label="Active" onChange={(v) => set("active", v)} />
        </div>
      </div>

      <CodeField label="Prompt" value={form.prompt} onChange={(v) => set("prompt", v)} rows={8} />
      <CodeField label="JSON schema (optional)" value={schemaText} onChange={setSchemaText} json rows={8} placeholder="{ }" />

      <div className="tw-flex tw-justify-end">
        <Button variant="primary" size="sm" leftIcon={FaSave} loading={saving} onClick={save}>Save</Button>
      </div>
    </div>
  );
};
PromptEditBody.propTypes = { prompt: PropTypes.object.isRequired, onSaved: PropTypes.func };

const ExtractionPromptConfigDrawer = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const prompts = useSelector((s) => s.admin_document_extraction.prompts) || [];
  const [openKeys, setOpenKeys] = useState([]);

  // Single-open accordion: opening one section collapses the others, so only the
  // active section's Save button is ever visible (no repeated Save buttons).
  const handleAccordionChange = (next) => {
    const added = next.find((k) => !openKeys.includes(k));
    setOpenKeys(added ? [added] : []);
  };

  useEffect(() => { if (show) dispatch(getExtractionPrompts()); }, [show, dispatch]);

  return (
    <Drawer show={show} onHide={onClose} title={<><FaCog className="tw-text-blue-600" /> Extraction prompt config</>} width="55%">
      <Alert type="info" className="tw-mb-3">
        Per document type: which AI provider/model runs, the prompt, vision mode, and JSON schema.
        Leave provider blank to inherit the global <strong>AI_PROVIDER</strong>.
      </Alert>

      {prompts.length === 0 ? (
        <div className="tw-text-center tw-py-10 tw-text-slate-400 tw-text-sm">No prompt configurations found.</div>
      ) : (
        <Accordion activeKeys={openKeys} onChange={handleAccordionChange}>
          {prompts.map((p) => (
            <Accordion.Item key={p.id} eventKey={p.id} header={<PromptHeader prompt={p} />}>
              <PromptEditBody prompt={p} onSaved={() => dispatch(getExtractionPrompts())} />
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Drawer>
  );
};
ExtractionPromptConfigDrawer.propTypes = { show: PropTypes.bool, onClose: PropTypes.func };

export default ExtractionPromptConfigDrawer;
